import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, Handshake, ShieldCheck, Sprout } from "lucide-react";
import { api } from "@/lib/apiClient";
import JobCard from "@/components/site/JobCard";

const HERO_IMG = "https://images.pexels.com/photos/18782448/pexels-photo-18782448.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SERVICES_IMG = "https://images.unsplash.com/photo-1608429835892-30be51ea4d6c?auto=format&fit=crop&w=1200&q=80";
const CONTRACTOR_IMG = "https://images.pexels.com/photos/5691686/pexels-photo-5691686.jpeg?auto=compress&cs=tinysrgb&w=1200";

const testimonials = [
  { name: "Klara M.", role: "Farm Ops · Berlin → Austin", text: "They handled my visa paperwork end-to-end. I was on a Texas farm within 3 months." },
  { name: "Tomasz W.", role: "Construction · Berlin → Asheville", text: "Real Berlin office, real US jobs. Not another recruiter — actual contract owners." },
  { name: "Nia B.", role: "Data · Berlin → Remote", text: "Best contract I've had. The Berlin team just gets remote hybrid work." },
  { name: "Julius R.", role: "Livestock · Berlin → Madison", text: "Housing sorted, kids in school, dairy running. It actually worked." },
  { name: "Farida A.", role: "Compliance · Berlin → Denver", text: "USDA grants used to scare me. Now I run them." },
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/jobs").then((r) => setFeatured((r.data || []).slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 sm:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.28em] font-semibold text-[#D35400] mb-6">
              Berlin, DE · Contracts for the United States
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter font-semibold text-[#1A2E24]">
              Berlin craft.
              <br />
              American soil.
              <br />
              <span className="text-[#D35400]">Contract work that moves you.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg text-[#5C6B64] leading-relaxed">
              Tool Cool Farm & Homestead Experts is a Berlin-based contracting firm that helps
              skilled workers relocate to the United States — with visa sponsorship, housing,
              and honest contracts on real farms and homesteads.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/jobs"
                data-testid="hero-cta-jobs"
                className="inline-flex items-center gap-2 bg-[#1A2E24] text-[#F9F6F0] px-7 py-4 text-sm font-medium tracking-wide hover:bg-[#2C4A3B] transition-transform hover:-translate-y-0.5"
              >
                Browse open contracts <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/signup"
                data-testid="hero-cta-signup"
                className="inline-flex items-center gap-2 border border-[#1A2E24] text-[#1A2E24] px-7 py-4 text-sm font-medium tracking-wide hover:bg-[#1A2E24] hover:text-[#F9F6F0] transition-colors"
              >
                Join the network
              </Link>
            </div>
            <div className="mt-16 grid grid-cols-3 gap-8 max-w-lg">
              {[
                { k: "127", v: "Contractors placed" },
                { k: "18", v: "US states covered" },
                { k: "94%", v: "Visa approval rate" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-display text-3xl sm:text-4xl font-semibold text-[#1A2E24]">{s.k}</div>
                  <div className="text-xs uppercase tracking-widest text-[#5C6B64] mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden border border-[#DCD7C9]">
              <img src={HERO_IMG} alt="Berlin architecture" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="text-xs uppercase tracking-[0.2em] opacity-90">HQ · Mitte, Berlin</div>
                <div className="font-display text-xl mt-1">A door in Berlin, an opportunity in America.</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden sm:block bg-[#D35400] text-white p-5 max-w-[220px]">
              <div className="text-xs uppercase tracking-widest opacity-80">Latest</div>
              <div className="font-display text-lg mt-1">6 new contracts this week</div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US - Bento */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.28em] font-semibold text-[#D35400]">Why us</div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#1A2E24]">
              We built this because we needed it ourselves.
            </h2>
            <p className="mt-4 text-[#5C6B64] leading-relaxed">
              Founded by Berliners who moved to America and hated the process. Now we do it
              better for the next wave of contractors.
            </p>
          </div>

          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { icon: Compass, title: "Berlin ↔ US pipeline", body: "Direct contracts from vetted US farms & operators. No agency middlemen skimming your rate." },
              { icon: ShieldCheck, title: "Visa done for you", body: "In-house immigration partners handle H-1B, O-1, and TN applications." },
              { icon: Handshake, title: "Real contracts", body: "Every position has a signed contract, a paycheck, and a start date. Full transparency." },
              { icon: Sprout, title: "Homestead specialty", body: "We focus on farming, homesteads, construction, and rural tech — where craft still matters." },
            ].map((f) => (
              <div key={f.title} className="border border-[#DCD7C9] bg-white p-6 sm:p-8">
                <f.icon className="text-[#D35400]" size={24} strokeWidth={1.5} />
                <div className="font-display text-lg mt-4 text-[#1A2E24]">{f.title}</div>
                <p className="text-sm text-[#5C6B64] mt-2 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED JOBS */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 border-t border-[#DCD7C9]">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6 mb-12">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] font-semibold text-[#D35400]">Open contracts</div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#1A2E24]">Fresh from the Berlin desk.</h2>
          </div>
          <Link data-testid="home-see-all-jobs" to="/jobs" className="text-sm text-[#1A2E24] underline underline-offset-4 hover:text-[#D35400]">
            See all open positions →
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-4">
          {featured.map((j, i) => (<JobCard key={j.id} job={j} index={i} />))}
          {!featured.length && (
            <div className="text-sm text-[#5C6B64] py-12 text-center border border-dashed border-[#DCD7C9]">Loading contracts…</div>
          )}
        </div>
      </section>

      {/* SERVICES SPLIT */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 relative aspect-[4/3] overflow-hidden">
          <img src={SERVICES_IMG} alt="Homestead" className="h-full w-full object-cover" />
        </div>
        <div className="lg:col-span-6">
          <div className="text-xs uppercase tracking-[0.28em] font-semibold text-[#D35400]">What we do</div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#1A2E24]">
            Contracts, relocation, and the boring paperwork.
          </h2>
          <ul className="mt-8 divide-y divide-[#DCD7C9] border-t border-b border-[#DCD7C9]">
            {[
              ["Contract sourcing", "Direct-to-farm US contracts, negotiated in Berlin."],
              ["Visa & immigration", "H-1B, O-1, TN, and family visa support."],
              ["Relocation logistics", "Flights, first-month housing, orientation."],
              ["Ongoing advocacy", "We stay involved — your Berlin office in the US."],
            ].map(([t, b]) => (
              <li key={t} className="py-5 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-8">
                <div className="font-display text-lg text-[#1A2E24] sm:w-56 shrink-0">{t}</div>
                <div className="text-sm text-[#5C6B64] leading-relaxed">{b}</div>
              </li>
            ))}
          </ul>
          <Link
            to="/services"
            data-testid="home-services-link"
            className="mt-8 inline-flex items-center gap-2 text-[#1A2E24] hover:text-[#D35400] text-sm font-medium"
          >
            Explore services <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      {/* TESTIMONIAL MARQUEE */}
      <section className="py-24 bg-[#F1EFE7] border-y border-[#DCD7C9] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 mb-12">
          <div className="text-xs uppercase tracking-[0.28em] font-semibold text-[#D35400]">Voices from the field</div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#1A2E24] max-w-2xl">
            Contractors who took the leap.
          </h2>
        </div>
        <div className="overflow-hidden">
          <div className="flex gap-24 brand-marquee whitespace-nowrap w-max">
            {[...testimonials, ...testimonials].map((t, i) => (
              <div key={i} className="whitespace-normal max-w-md">
                <div className="font-display text-xl sm:text-2xl text-[#1A2E24] leading-snug">"{t.text}"</div>
                <div className="mt-3 text-xs uppercase tracking-widest text-[#5C6B64]">{t.name} — {t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24">
        <div className="relative overflow-hidden bg-[#1A2E24] text-[#F9F6F0] p-10 sm:p-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <img src={CONTRACTOR_IMG} alt="Contractor" className="absolute inset-0 h-full w-full object-cover opacity-20" />
          <div className="relative md:col-span-8">
            <div className="text-xs uppercase tracking-[0.28em] font-semibold text-[#D35400]">Ready to move?</div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium">
              Your Berlin office. Your American contract. One team.
            </h2>
          </div>
          <div className="relative md:col-span-4 flex flex-col gap-4 md:items-end">
            <Link data-testid="cta-signup-bottom" to="/signup" className="inline-flex items-center gap-2 bg-[#D35400] text-white px-7 py-4 text-sm font-medium hover:bg-[#E67E22]">
              Join the network <ArrowUpRight size={16} />
            </Link>
            <Link data-testid="cta-contact-bottom" to="/contact" className="inline-flex items-center gap-2 border border-[#F9F6F0] text-[#F9F6F0] px-7 py-4 text-sm font-medium hover:bg-[#F9F6F0] hover:text-[#1A2E24]">
              Talk to Berlin
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
